import type { SiteLocation } from "~/store/siteSlice";

import { useEffect, useMemo, useRef, useState } from "react";

import { useGetSiteLocationsPaginatedQuery } from "~/services/endpoints/siteApi";
import { BpfTreeType, SortEnumType, useGetMyBpfStartingNodesQuery } from "~/services/graphql";

/**
 * Page size for each `siteLocations` request. wsapi caps this query at `maxAllowedItems: 50`
 * (HC0051 on anything larger) — every page, first and subsequent, requests this same size rather
 * than assuming a single page can ever hold an arbitrarily large grant set (P3-F1).
 */
const PAGE_SIZE = 50;

const ORDER = [{ name: SortEnumType.Asc }];

interface UseStartingSitesResult {
	sites: SiteLocation[];
	isLoading: boolean;
	isError: boolean;
}

/**
 * Resolves the selectable starting sites for the site picker (RequireSite / SelectSite). Reads the
 * authenticated user's BPF starting-node grants (`myBpfStartingNodes`, the parity replacement for
 * the legacy treeForUser), filters to the requested tree, and resolves the granted station ids to
 * human-readable names via `siteLocations`, auto-fetching every remaining page (Atlas: Auto-paging
 * hook for cursor-based paginated RTK Query) so a grant set of any size resolves correctly instead of
 * requesting more than the server's page-size cap in one call.
 *
 * This is what makes the shell usable past login: the foundation ships RequireSite enforced with
 * zero options, so without this query a field user can never reach a feature area.
 */
const useStartingSites = (treeType: BpfTreeType = BpfTreeType.Finish): UseStartingSitesResult => {
	const startingNodesQuery = useGetMyBpfStartingNodesQuery();

	const grantedSiteIds = useMemo(() => {
		const nodes = startingNodesQuery.data?.myBpfStartingNodes?.startingNodes ?? [];
		return Array.from(new Set(nodes.filter((node) => node.treeType === treeType).map((node) => node.siteLocationId)));
	}, [startingNodesQuery.data, treeType]);

	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const idsKey = JSON.stringify(grantedSiteIds);

	// Render-phase cursor reset: a grant-set change must restart pagination from page 1 in the SAME
	// render that changes the ids, never in a post-render effect — otherwise the stale cursor leaks
	// into the new where-keyed cache entry and corrupts the merged accumulation.
	const prevIdsKey = useRef(idsKey);
	const effectiveCursor = prevIdsKey.current === idsKey ? cursor : undefined;
	if (prevIdsKey.current !== idsKey) {
		prevIdsKey.current = idsKey;
		setCursor(undefined);
	}

	// Only resolve names once we know which stations the user is granted; skip the call otherwise.
	const siteLocationsQuery = useGetSiteLocationsPaginatedQuery(
		{ after: effectiveCursor, first: PAGE_SIZE, order: ORDER, where: { siteLocationId: { in: grantedSiteIds } } },
		{ skip: grantedSiteIds.length === 0 }
	);

	// Auto-fetch every remaining page; stop advancing on error so a bad page doesn't retry forever.
	useEffect(() => {
		if (siteLocationsQuery.isError) return;
		const pageInfo = siteLocationsQuery.data?.siteLocations?.pageInfo;
		if (pageInfo?.hasNextPage && !siteLocationsQuery.isFetching) {
			const nextCursor = pageInfo.endCursor;
			if (nextCursor && nextCursor !== cursor) {
				setCursor(nextCursor);
			}
		}
	}, [cursor, siteLocationsQuery.data, siteLocationsQuery.isError, siteLocationsQuery.isFetching]);

	const sites = useMemo<SiteLocation[]>(() => {
		const nodes = siteLocationsQuery.data?.siteLocations?.nodes ?? [];
		return nodes.map((node) => ({ id: node.siteLocationId, name: node.name, erpId: node.erpId }));
	}, [siteLocationsQuery.data]);

	return {
		sites,
		isLoading: startingNodesQuery.isLoading || siteLocationsQuery.isLoading,
		isError: startingNodesQuery.isError || siteLocationsQuery.isError
	};
};

export default useStartingSites;
